import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccessGate } from "@/lib/access";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Music2, Play, RefreshCw } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { FilePicker } from "@/components/music/FilePicker";
import { MusicResult } from "@/components/music/MusicResult";
import { MusicJobList } from "@/components/music/MusicJobList";
import { translateError } from "@/lib/errors";
import type { MontageAspectRatio } from "@/lib/montageApi";
import {
  createMusicJob, deleteMusicJob, getMusicJob, listMusicJobs, MUSIC_CLIENT_TIMEOUT_MS, type MusicJob,
} from "@/lib/musicApi";

const IMAGE_RE = /\.(jpe?g|png|webp)$/i;
const AUDIO_RE = /\.(mp3|wav|m4a|aac|ogg)$/i;
const MAX_IMAGE = 15 * 1024 * 1024;
const MAX_AUDIO = 60 * 1024 * 1024;
const POLL_MS = 3000;

export default function Music() {
  const { t } = useTranslation();
  const [image, setImage] = useState<File | null>(null);
  const [audio, setAudio] = useState<File | null>(null);
  const [aspect, setAspect] = useState<MontageAspectRatio>("9:16");
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [jobs, setJobs] = useState<MusicJob[]>([]);
  const [current, setCurrent] = useState<MusicJob | null>(null);
  const [slow, setSlow] = useState(false);
  const startedAt = useRef<number | null>(null);

  const { guard, isAuthenticated } = useAccessGate();
  const refresh = useCallback(() => (isAuthenticated ? listMusicJobs().then(setJobs).catch(() => {}) : Promise.resolve(setJobs([]))), [isAuthenticated]);
  useEffect(() => { refresh(); }, [refresh]);

  const processing = current?.status === "PROCESSING";

  // Poll the active job; after the client wait budget stop polling (the backend job keeps running).
  useEffect(() => {
    if (!processing || !current || slow) return;
    const timer = setInterval(async () => {
      try {
        const j = await getMusicJob(current.id);
        setCurrent(j);
        if (j.status !== "PROCESSING") {
          refresh();
          if (j.status === "READY") toast.success(t("music.created"));
          else toast.error(translateError(j.error, "music.failed"));
        } else if (startedAt.current && Date.now() - startedAt.current > MUSIC_CLIENT_TIMEOUT_MS) {
          setSlow(true);
        }
      } catch { /* transient: keep polling */ }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [processing, current, slow, refresh, t]);

  const pickImage = (f: File) => {
    if (!IMAGE_RE.test(f.name)) return toast.error(t("music.unsupportedImage"));
    if (f.size > MAX_IMAGE) return toast.error(t("music.imageTooLarge"));
    setImage(f);
  };
  const pickAudio = (f: File) => {
    if (!AUDIO_RE.test(f.name)) return toast.error(t("music.unsupportedAudio"));
    if (f.size > MAX_AUDIO) return toast.error(t("music.audioTooLarge"));
    setAudio(f);
  };

  const create = async () => {
    if (!image || !audio || uploadPct !== null || processing) return;
    if (!(await guard())) return; // login required BEFORE any upload/processing (FREE tool, no subscription)
    setUploadPct(0);
    setSlow(false);
    try {
      const job = await createMusicJob(image, audio, aspect, setUploadPct);
      startedAt.current = Date.now();
      setCurrent(job);
      setImage(null);
      setAudio(null);
      refresh();
    } catch (err: any) {
      toast.error(translateError(err?.response?.data?.detail, "music.failed"));
    } finally {
      setUploadPct(null);
    }
  };

  const checkAgain = async () => {
    if (!current) return;
    try {
      const j = await getMusicJob(current.id);
      setCurrent(j);
      if (j.status === "PROCESSING") toast.info(t("music.stillProcessing"));
      else { setSlow(false); refresh(); }
    } catch { toast.error(t("errors.generic")); }
  };

  const remove = async (job: MusicJob) => {
    if (!(await guard())) return;
    if (!window.confirm(t("music.deleteConfirm"))) return;
    try {
      await deleteMusicJob(job.id);
      setJobs((p) => p.filter((j) => j.id !== job.id));
      if (current?.id === job.id) setCurrent(null);
    } catch (err: any) {
      toast.error(translateError(err?.response?.data?.detail));
    }
  };

  const busy = uploadPct !== null || processing;
  const phaseLabel = current?.phase === "storing" ? t("music.phaseStoring") : t("music.phaseEncoding");

  return (
    <Layout>
      <main data-testid="music-page" className="relative mx-auto max-w-3xl px-6 pb-32 pt-16 sm:px-10 lg:pt-20">
        <header className="relative">
          <span className="eyebrow">{t("music.eyebrow")}</span>
          <h1 className="headline-shadow mt-5 font-display text-4xl font-bold uppercase leading-[0.98] tracking-wide sm:text-5xl lg:text-6xl">
            <span className="text-neon-fuchsia">{t("nav.music")}</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm text-[#B8C4DA] sm:text-base">{t("music.subtitle")}</p>
        </header>

        <section data-testid="music-panel" className="glass-fuchsia relative mt-10 rounded-2xl p-6 sm:p-8">
          <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[#FF7AD5]/80 to-transparent" />
          <ol className="space-y-3">
            <li>
              <FilePicker testid="music-pick-image" label={`1 · ${t("music.chooseImage")}`} hint={t("music.imageFormats")}
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" icon={<ImageIcon className="h-5 w-5" />}
                file={image} disabled={busy} onPick={pickImage} />
            </li>
            <li>
              <FilePicker testid="music-pick-audio" label={`2 · ${t("music.chooseAudio")}`} hint={t("music.audioFormats")}
                accept=".mp3,.wav,.m4a,.aac,.ogg,audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg" icon={<Music2 className="h-5 w-5" />}
                file={audio} disabled={busy} onPick={pickAudio} />
            </li>
          </ol>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div role="radiogroup" aria-label={t("music.format")} className="seg">
              {(["9:16", "16:9"] as const).map((v) => (
                <button key={v} type="button" role="radio" aria-checked={aspect === v} data-testid={`music-aspect-${v.replace(":", "-")}`}
                  onClick={() => setAspect(v)} disabled={busy}
                  className={"seg-btn " + (aspect === v ? "seg-btn-active" : "disabled:cursor-not-allowed disabled:opacity-50")}>
                  {v === "9:16" ? t("format.vertical") : t("format.horizontal")} {v}
                </button>
              ))}
            </div>
            <Button data-testid="music-create-btn" size="lg" className="btn-fuchsia" onClick={create} disabled={!image || !audio || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {uploadPct !== null ? t("montageEditor.uploadingPct", { pct: uploadPct }) : processing ? t("music.creating") : `3 · ${t("music.create")}`}
            </Button>
          </div>

          {processing && (
            <div data-testid="music-processing" className="mt-6 rounded-xl border border-[#FF3DBE]/25 bg-[#0B0412]/70 px-4 py-3 text-sm">
              <p className="flex items-center gap-2 text-[#FF9BE0]"><Loader2 className="h-4 w-4 animate-spin" /> {phaseLabel}</p>
              {slow && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span data-testid="music-slow-notice">{t("music.takingLonger")}</span>
                  <Button data-testid="music-check-again-btn" variant="secondary" size="sm" onClick={checkAgain}>
                    <RefreshCw className="h-3.5 w-3.5" /> {t("music.checkAgain")}
                  </Button>
                </div>
              )}
            </div>
          )}
          {current?.status === "ERRORE" && (
            <p data-testid="music-error" className="mt-6 text-sm text-[#FF5C6C]">{translateError(current.error, "music.failed")}</p>
          )}
          {current?.status === "READY" && <div className="mt-8"><MusicResult job={current} /></div>}
        </section>

        <MusicJobList jobs={jobs} selectedId={current?.id ?? null} onSelect={setCurrent} onDelete={remove} />
      </main>
    </Layout>
  );
}
